import { Asset } from '@thehubz/client/data-access-api';
import { createAssetS3Key, createVersionString } from '@thehubz/shared/model';
import { format, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import { AssetPublishingStatus, AssetUIModel } from '../models';

/**
 * Method to check if asset is planned to be published or already unpublished
 *
 * @param asset
 */
export function isAssetPlannedOrUnpublished(asset: AssetUIModel): boolean {
  const publishingStatus = getAssetPublishingStatus(asset);
  const statuses = [AssetPublishingStatus.PLANNED, AssetPublishingStatus.UNPUBLISHED];
  return statuses.includes(publishingStatus);
}

/**
 * Get the publishing status of the asset
 */
export const getAssetPublishingStatus = (asset: AssetUIModel): AssetPublishingStatus => {
  if (!asset) return AssetPublishingStatus.NOT_SET;
  const { publishStartDate, publishEndDate } = asset;
  const currentDate = startOfDay(new Date());
  const publishStartDateObj = publishStartDate ? startOfDay(new Date(publishStartDate)) : null;
  const publishEndDateObj = publishEndDate ? startOfDay(new Date(publishEndDate)) : null;

  if (!publishStartDateObj && !publishEndDateObj) {
    return AssetPublishingStatus.NOT_SET;
  }

  if (publishStartDateObj && isBefore(currentDate, publishStartDateObj)) {
    return AssetPublishingStatus.PLANNED;
  } else if (publishStartDateObj && isSameDay(currentDate, publishStartDateObj)) {
    return AssetPublishingStatus.PUBLISHED;
  } else if (publishEndDateObj && (isBefore(currentDate, publishEndDateObj) || isSameDay(currentDate, publishEndDateObj))) {
    return AssetPublishingStatus.PUBLISHED;
  } else if (publishEndDateObj && isAfter(currentDate, publishEndDateObj)) {
    return AssetPublishingStatus.UNPUBLISHED;
  } else {
    return AssetPublishingStatus.NOT_SET;
  }
};

/**
 * Get the publishing message of the asset based on the publishing start and/or end dates
 */
export const getAssetPublishingMessage = (asset: AssetUIModel): string => {
  const publishingStatus = getAssetPublishingStatus(asset);

  switch (publishingStatus) {
    case AssetPublishingStatus.PLANNED:
      return asset.publishStartDate ? $localize`Planned for publishing on` + ` ${format(new Date(asset.publishStartDate), 'dd MMM yyyy')}` : '';
    case AssetPublishingStatus.PUBLISHED:
      if (asset.publishEndDate) {
        return $localize`Planned for unpublishing on` + ` ${format(new Date(asset.publishEndDate), 'dd MMM yyyy')}`;
      } else if (asset.publishStartDate) {
        return $localize`Published on` + ` ${format(new Date(asset.publishStartDate), 'dd MMM yyyy')}`;
      } else {
        return '';
      }
    case AssetPublishingStatus.UNPUBLISHED:
      return asset.publishEndDate ? $localize`Unpublished on` + ` ${format(new Date(asset.publishEndDate), 'dd MMM yyyy')}` : '';
    default:
      return '';
  }
};

/**
 * Create a upload key (folder in S3 bucket) for the asset including the version.
 *
 * @param asset
 * @returns A file key
 */
export function createAssetFileKey(asset: AssetUIModel, overrideName?: string, uploadFlag?: boolean, version?: number) {
  // the asset is created before we upload the file, so latest version is the one we try to upload
  if (!asset.mainFile) {
    throw new Error(`the asset ${asset._id} does not contain any file`);
  }
  // hold the asset file path of the s3 bucket
  let assetFileKey = '';
  const assetVersion = uploadFlag ? asset.mainFile.version + 1 : asset.mainFile.version;
  const assetAspectRatio = asset.mainFile.defaultAspectRatio;
  const assetFileFormat = asset.mainFile.defaultFileFormat;
  const versionString = createVersionString(version || assetVersion);
  // check is for the banner's fallback image that get generated inside 'gen-preview' path
  // else: is for the rest of the asset
  if (asset.assetType === Asset.AssetTypeEnum.HtmlBanner && overrideName) {
    assetFileKey = createAssetS3Key(asset._id, versionString, assetAspectRatio, `gen-preview/${overrideName}`);
  } else {
    assetFileKey = createAssetS3Key(asset._id, versionString, assetAspectRatio, overrideName || assetFileFormat + '.' + asset.mainFile.ext);
  }
  return assetFileKey;
}
